import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, ImagePlus, Loader2 } from "lucide-react";

/**
 * Drag-drop or click-to-select image uploader.
 * Uploads to /api/uploads/image (Emergent Object Storage) and calls
 * onUploaded(url) with the resulting public URL.
 *
 * Props:
 *   value       – current image URL (shows preview + clear button)
 *   onUploaded  – (url) => void
 *   onCleared   – () => void  (optional)
 *   label       – accessible label / placeholder
 *   testid      – data-testid prefix
 */
export default function ImageUploader({ value, onUploaded, onCleared, label = "Upload image", testid = "img-upload" }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
      toast.success("Uploaded");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (ev) => {
    ev.preventDefault();
    setDragging(false);
    handleFiles(ev.dataTransfer.files);
  };

  if (value) {
    return (
      <div className="relative border border-white/20 bg-[#0B1120] group" data-testid={`${testid}-preview`}>
        <img src={value} alt={label} className="w-full h-48 object-cover" />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
          <button
            type="button" onClick={() => inputRef.current?.click()}
            data-testid={`${testid}-replace`}
            className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 bg-black/70 hover:bg-brand-blue hover:text-white"
          >
            <ImagePlus size={12} /> Replace
          </button>
          <button
            type="button" onClick={() => onCleared && onCleared()}
            data-testid={`${testid}-clear`}
            className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest border border-red-500/60 text-red-400 px-3 py-2 bg-black/70 hover:bg-red-500 hover:text-white"
          >
            <X size={12} /> Remove
          </button>
        </div>
        <input
          ref={inputRef} type="file" accept="image/*" hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      data-testid={testid}
      className={`border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 py-10 px-6 text-center transition ${
        dragging ? "border-brand-blue bg-brand-blue/5" : "border-white/20 hover:border-white/40 bg-[#0B1120]"
      }`}
    >
      {busy ? (
        <>
          <Loader2 size={22} className="brand-blue animate-spin" />
          <div className="text-xs font-mono uppercase tracking-widest brand-blue">Uploading…</div>
        </>
      ) : (
        <>
          <UploadCloud size={24} className={dragging ? "brand-blue" : "text-white/50"} />
          <div className="text-xs font-mono uppercase tracking-widest text-white/60">{label}</div>
          <div className="text-[10px] text-white/40">Drag & drop or click · JPG/PNG/WEBP · max 5MB</div>
        </>
      )}
      <input
        ref={inputRef} type="file" accept="image/*" hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
